import React, { FC, useEffect, useMemo, useState } from 'react';

import { FormattedMessage, useIntl } from 'react-intl';
import { useNavigate, useParams } from 'react-router-dom';

import { hockeyFormatter } from '@nhl/formatter-utilities';
import { BaseStyle, IdEnums, ShortCodeEnums, ReactTable, SelectTypes, CoreUtils } from '@nhl/nhl-breakaway-ui';
import LoadingDisplay from '@nhl/nhl-react-ui/lib/LoadingDisplay';
import { ColumnSort } from '@tanstack/react-table';

import CommonMessages from '~/core/commonMessages';
import { useIsWebview } from '~/core/hooks/useIsWebview';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';
import NoContent from '~/views/components/noContent/NoContent';
import PageWrapper from '~/views/components/pageWrapper/PageWrapper';

import { useClubSeasonsHistoryQuery, useClubStatsForSeasonQuery } from './api';
import { goalieColumns, skaterColumns } from './columns';
import { SeasonInfo } from './types';
import { didTeamPlayGamesOfThisType } from './utils';

import { ClubStatsTableContainer, Header, PositionTitle, SelectContainer, StyledSelect } from './styles';

const defaultPlayerSort: Array<ColumnSort> = [
    { id: 'points', desc: true },
    { id: 'goals', desc: true },
    { id: 'gamesPlayed', desc: false },
];

const defaultGoalieSort: Array<ColumnSort> = [
    { id: 'gamesPlayed', desc: true },
    { id: 'goalsAgainstAverage', desc: false },
];

const NoPlayoffsText = (
    <FormattedMessage
        description="There are no playoff games scheduled for this club."
        defaultMessage="There are no playoff games scheduled for this club."
    />
);

const NoPreseasonText = (
    <FormattedMessage
        description="There are no preseason games scheduled for this club."
        defaultMessage="There are no preseason games scheduled for this club."
    />
);

const NoRegularSeasonText = (
    <FormattedMessage
        description="A message for when a club hasn't yet played a regular season game"
        defaultMessage="This club has not yet played a game in this regular season."
    />
);

const NoGamesComponents = {
    [IdEnums.GameType.preseason]: NoPreseasonText,
    [IdEnums.GameType.regularSeason]: NoRegularSeasonText,
    [IdEnums.GameType.playoffs]: NoPlayoffsText,
};

const ClubStats: FC = () => {
    const isWebview = useIsWebview();
    const params = useParams();
    const navigate = useNavigate();
    const screenSize = useSizeWithThrottle();
    const intl = useIntl();

    const teamAbbrev = ShortCodeEnums.TeamTriCode[window.__SITE_SETTINGS__.franchise.triCode.toUpperCase()];

    const { season: seasonFromUrl, gameType: gameTypeFromUrl } = params;

    const gameType = gameTypeFromUrl ? (Number(gameTypeFromUrl) as IdEnums.GameType) : IdEnums.GameType.regularSeason; // default to regular season

    const [availableGameTypes, setAvailableGameTypes] = useState([
        IdEnums.GameType.preseason,
        IdEnums.GameType.regularSeason,
        IdEnums.GameType.playoffs,
    ]);
    const [seasonsData, setSeasonsData] = useState<Array<SeasonInfo>>([]);

    const { data: clubSeasonsData, isLoading: isClubSeasonsHistoryLoading } =
        useClubSeasonsHistoryQuery(teamAbbrev) || [];
    const { data, isLoading, resolvedSeason } = useClubStatsForSeasonQuery(teamAbbrev, seasonFromUrl, gameType);

    useEffect(() => {
        if (Array.isArray(clubSeasonsData) && resolvedSeason) {
            setSeasonsData(clubSeasonsData);

            const currentSeason: SeasonInfo | undefined = clubSeasonsData.find((s) => s.season === resolvedSeason);

            const types = currentSeason?.gameTypes;
            if (types) {
                setAvailableGameTypes(types);
            }
        }
    }, [clubSeasonsData, seasonsData, resolvedSeason]);

    const handleSeasonChange = (season: SelectTypes.BasicOption, replace?: boolean) => {
        navigate(`/stats/${season.value}`, { replace });
        setAvailableGameTypes(seasonsData.find((seasonData) => seasonData.season === season.value)?.gameTypes || []);
    };

    const handleGameTypeChange = (gameType: SelectTypes.BasicOption) => {
        navigate(`/stats/${resolvedSeason}/${gameType.value}`);
    };

    const skaterCols = useMemo(
        () => skaterColumns(screenSize.width, false, isWebview, intl),
        [intl, isWebview, screenSize.width]
    );
    const goalieCols = useMemo(
        () => goalieColumns(screenSize.width, true, isWebview, intl),
        [intl, isWebview, screenSize.width]
    );

    const StatsTables = () => (
        <>
            <PositionTitle aria-level={2}>
                <FormattedMessage {...CommonMessages.playerInfoDescriptions.long.skaters} />
            </PositionTitle>
            <ClubStatsTableContainer>
                <ReactTable columns={skaterCols} data={data?.skaters || []} initialSorting={defaultPlayerSort} />
            </ClubStatsTableContainer>
            <PositionTitle aria-level={2}>
                <FormattedMessage {...CommonMessages.playerInfoDescriptions.long.goalies} />
            </PositionTitle>
            <ClubStatsTableContainer>
                <ReactTable columns={goalieCols} data={data?.goalies || []} initialSorting={defaultGoalieSort} />
            </ClubStatsTableContainer>
        </>
    );

    const pageTitle = useMemo(
        () =>
            intl.formatMessage(
                {
                    description: 'Title for the Club Stats page, to be displayed on a browser tab etc.',
                    defaultMessage: '{clubName} Club Stats | {season} | {clubName}',
                },
                {
                    clubName: window.__SITE_SETTINGS__.franchise.name,
                    season: resolvedSeason ? hockeyFormatter.singleSeasonFormatter(resolvedSeason) : '',
                }
            ),
        [intl, resolvedSeason]
    );

    const seasonOptions: Array<SelectTypes.BasicOption> = useMemo(
        () =>
            seasonsData?.map<SelectTypes.BasicOption>((seasonData) => ({
                key: seasonData.season,
                label: hockeyFormatter.singleSeasonFormatter(seasonData.season),
                value: seasonData.season,
            })),
        [seasonsData]
    );

    const gameTypeOptions: Array<SelectTypes.BasicOption> = useMemo(
        () =>
            availableGameTypes?.map((type, i) => ({
                key: i,
                value: type,
                label: CoreUtils.safeFormatMessage(intl, CommonMessages.gameTypeDescriptions.long[type]),
            })),
        [intl, availableGameTypes]
    );

    const ariaLabels = useMemo(() => {
        const labels = {
            gameTypeSelect: {
                dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.season.dropdownButton),
                optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.season.optionsBox),
            },
            seasonSelect: {
                dropdownButton: intl.formatMessage(CommonMessages.ariaLabels.select.season.dropdownButton),
                optionsBox: intl.formatMessage(CommonMessages.ariaLabels.select.season.optionsBox),
            },
        };
        return labels;
    }, [intl]);

    return isLoading || isClubSeasonsHistoryLoading ? (
        <LoadingDisplay isLoading={true} />
    ) : (
        <PageWrapper theme={BaseStyle.LightTheme} pageTitle={pageTitle}>
            <>
                <Header>
                    <h1>
                        <FormattedMessage defaultMessage="Statistics" description="Club stats title" />
                    </h1>
                    {window.__AD_SETTINGS__.presbyDivId && (
                        <AdTag divId={window.__AD_SETTINGS__.presbyDivId} adType={AdType.PRESBY} />
                    )}
                </Header>
                <SelectContainer>
                    <StyledSelect
                        className="season-select"
                        dropdownButtonAriaLabel={ariaLabels.seasonSelect.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) => handleSeasonChange(value)}
                        options={seasonOptions}
                        optionsBoxAriaLabel={ariaLabels.seasonSelect.optionsBox}
                        selectedOption={
                            resolvedSeason
                                ? seasonOptions.find((seasonOption) => seasonOption.value === resolvedSeason)
                                : seasonOptions[0]
                        }
                    />
                    <StyledSelect
                        className="game-type-select"
                        dropdownButtonAriaLabel={ariaLabels.gameTypeSelect.dropdownButton}
                        onOptionChange={(_, value: SelectTypes.BasicOption) => handleGameTypeChange(value)}
                        options={gameTypeOptions}
                        optionsBoxAriaLabel={ariaLabels.gameTypeSelect.optionsBox}
                        selectedOption={gameTypeOptions.find(
                            (gameTypeOption) => gameTypeOption.value === data.gameType
                        )}
                    />
                </SelectContainer>

                {didTeamPlayGamesOfThisType(gameType, availableGameTypes, data) ? (
                    <StatsTables />
                ) : (
                    <NoContent primaryText={NoGamesComponents[gameType]} />
                )}
            </>
        </PageWrapper>
    );
};

export { ClubStats as Component };
